import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { finalize, Subscription } from 'rxjs';
import { FormBuilder, FormGroup, NgForm } from '@angular/forms';
import { CustomErrorStateMatcher } from '../../helpers/customerrorstate';
import { NavigationEnd, Router } from '@angular/router';
import { HttpPaginatedDataSource } from '../../utils/HttpPaginatedDataSource';
import { MatSort, Sort } from '@angular/material/sort';
import { SpinnerService } from '../../service/spinner.service';
import { CartService } from '../../service/cart.service';
import { ExportService } from '../../service/export.service';
import { saveAs } from 'file-saver';
import { Cart } from '@/dto/cart/cart';
import { StatusCart } from '@/enum/statusCart';
import { ClientService } from '../../service/client.service';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { ToastController } from '@ionic/angular';

@Component({
  selector: 'app-cart',
  templateUrl: './cart.page.html',
  styleUrls: ['./cart.page.scss']
})
export class CartPage implements OnInit, OnDestroy {
  public esMatcher = new CustomErrorStateMatcher();
  private unsubscribe: Subscription[] = [];
  FilterForm: FormGroup;
  exportForm: FormGroup;

  public data: HttpPaginatedDataSource<any>;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('selectOption') selectOption: any;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  public pageIndex = 1;
  public pageSize: number;
  public sortCol = "creation_date";
  public sortDir = "desc";
  public currentLanguage: string;
  public pageSizeOpts: number[];
  public listCustomer: any[] = [];

  public StatusCart: typeof StatusCart = StatusCart;
  constructor(private router: Router, private formBuilder: FormBuilder,
    private spinnerService: SpinnerService, private cartService: CartService, private exportService: ExportService, private clientService: ClientService,
    private translateService: TranslateService, public toastController: ToastController) {
  }

  public columnsToDisplay = ['cart_id', 'barcode', 'client_reference', 'client', 'amount', 'status', 'creation_date', 'expiration_date'];

  ngOnInit() {

    this.data = new HttpPaginatedDataSource([]);
    this.data.sort = this.sort;
    this.data.paginator = this.paginator;
    this.pageSizeOpts = [5, 10, 15, 20, 100]
    this.pageSize = 15;
    this.pageIndex = 0;
    this.FilterForm = this.formBuilder.group({
      cart_id: [''],
      client_reference: [''],
      barcode: [''],
      client_id: [''],
      status: [''],
      minAmount: [''],
      maxAmount: [''],
      minDate: [''],
    });

    this.exportForm = this.formBuilder.group({
      typeExport: 'csv'
    })

    this.unsubscribe.push(this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd && event.url === '/cart') {
        this.unsubscribe.push(
          this.clientService.get().pipe(finalize(() => {
          })).subscribe(val => {
            this.listCustomer = val;
          })
        );
        this.onSubmit(this.pageSize, this.pageIndex, this.CreateCartFilter(), this.sortCol, this.sortDir);
      }
    }));

    this.sort.sortChange.subscribe((sort: Sort) => {
      this.paginator.pageIndex = 0;
      this.pageIndex = 0;
      this.sortCol = sort.active;
      this.sortDir = sort.direction;
      this.onSubmit(this.pageSize, this.pageIndex, this.CreateCartFilter(), this.sortCol, this.sortDir);
    });
  }

  // EXPORT DATA
  async handleExport() {
    const toast = await this.toastController.create({
      message: this.translateService.instant('Export.Text'),
      duration: 1000
    });
    toast.present();

    let typeForm = this.exportForm.controls.typeExport.value
    this.unsubscribe.push(this.exportService.getExportCart(typeForm, this.CreateCartFilter(), this.sortCol, this.sortDir).pipe(finalize(() => {
      this.spinnerService.hide();
    })).subscribe(data => { saveAs(data, 'cart.' + typeForm) }))
  }

  // SELECT OPTION CUSTOMER
  handleChangeCustomer(e) {
    let txt_placeholder = e.target.value || ''
    this.selectOption.nativeElement.value = ''
    this.FilterForm.patchValue({
      client_id: txt_placeholder
    })
    this.selectOption.nativeElement.setAttribute('placeholder', txt_placeholder)
  }

  get f() { return this.FilterForm.controls; }

  onSubmit(pageSize: number, pageIndex: number, cartFilter: Cart, sortCol: string, sortDir: string) {
    this.spinnerService.show();
    this.data.data = []
    this.unsubscribe.push(
      this.cartService.get(pageIndex, pageSize, cartFilter, sortCol, sortDir).pipe(finalize(() => {
        this.spinnerService.hide();
      })).subscribe(val => {
        this.data.data = val.data;
        this.data.sort = this.sort;
        this.data.sortingDataAccessor = (item, property) => {
          if (property === 'status') {
            let text = this.translateService.instant(StatusCart[item[property]]);
            return text.toLocaleLowerCase();
          } else if (typeof item[property] === 'string') {
            return item[property].toLocaleLowerCase();
          } else {
            return item[property];
          }
        };
        this.data._updatePaginator(val.total)
      }));
  }


  change(pageEvent: PageEvent) {
    if (this.pageIndex !== pageEvent.pageIndex || this.pageSize !== pageEvent.pageSize) {
      this.pageIndex = pageEvent.pageIndex;
      this.pageSize = pageEvent.pageSize;
      this.data = new HttpPaginatedDataSource([]);
      this.onSubmit(this.pageSize, this.pageIndex, this.CreateCartFilter(), this.sortCol, this.sortDir);
    }
  }


  goToDetails(path) {
    this.router.navigate(['/cart/' + path])
  }

  cancel(form: NgForm) {
    this.selectOption.nativeElement.value = '';
    this.selectOption.nativeElement.setAttribute('placeholder', this.translateService.instant('Cart.Text.SelectOne'));
    form.resetForm();
    this.FilterForm.reset();
    this.FilterForm.markAsUntouched();
    this.exportForm.patchValue({ typeExport: 'csv' });
  }

  private CreateCartFilter(): Cart {
    const cart = new Cart();
    cart.cart_id = this.f.cart_id.value
    cart.client_reference__icontains = this.f.client_reference.value
    cart.barcode = this.f.barcode.value
    cart.status = this.f.status.value
    cart.client_id = this.parseValueFromCustomerInput(this.f.client_id.value)
    cart['amount__gte'] = this.f.minAmount.value
    cart['amount__lte'] = this.f.maxAmount.value
    cart.creation_date = ''
    const startOfDay = new Date(this.f.minDate.value);
    startOfDay.setUTCHours(0, 0, 0, 0);


    const endOfDay = new Date(this.f.minDate.value);
    endOfDay.setUTCHours(23, 59, 59, 999);

    cart['creation_date__gte'] = this.f.minDate.value ? startOfDay.toISOString() : "";
    cart['creation_date__lte'] = this.f.minDate.value ? endOfDay.toISOString() : "";
    cart["lang"] = this.translateService.currentLang;
    return cart;
  }

  filterForm() {
    this.onSubmit(this.pageSize, this.pageIndex, this.CreateCartFilter(), this.sortCol, this.sortDir);
  }

  parseValueFromCustomerInput(value: string) {
    for (let i = 0; i < this.listCustomer.length; i++) {
      if (this.listCustomer[i].text == value) return this.listCustomer[i].id
    }
    return ""
  }

  ngOnDestroy() {
    this.unsubscribe.forEach(s => s.unsubscribe());
  }
}
